/**
 * PATH: packages/ui/src/kanban/components/email-from-card-parse.ts
 * FILE_ID: UI-KANBAN-EMAIL-FROMCARD-PARSE-001
 * PURPOSE: Pure I→O helpers that the EmailFromCard renderer composes
 *          to turn raw Kanban Card data + Trello comments into the
 *          shape an email-thread renderer needs.
 *
 * WHY: split out of EmailFromCard.tsx (2026-05-20) to keep the React
 *      component file under the 300-line hard limit. Every helper is
 *      a synchronous pure function — same input always returns the
 *      same output, no side effects, no hooks. Unit tests exercise
 *      them in isolation (see `EmailFromCard.test.ts`).
 *
 * FLOW:
 *   ┌─────────────┐   ┌──────────────────────┐   ┌────────────────┐
 *   │ card.title  │ → │ parseEmailTitle      │ → │ {name, addr}   │
 *   │ card.summary│ → │ stripBenkoPreamble   │ → │ body text      │
 *   │ comments[]  │ → │ parseEmailThreadFrom │ → │ thread items   │
 *   └─────────────┘   │   Comments()         │   └────────────────┘
 *                     └──────────────────────┘
 *
 * KEY CROSS-STACK CONTRACT:
 *   COMMENT_FROM_RE here MUST stay character-for-character identical
 *   to `COMMENT_FROM_RE` in
 *   `email_kanban/services/trello_card_email_linker.py` (backend
 *   linker). If the backend's regex changes, this one must change
 *   too — otherwise the operator sees a different sender on the
 *   Email tab vs. what the linker eventually attaches as
 *   `primary_email`, and that's a Trust-Surface bug.
 */
import type { TrelloCommentDetail } from '../types'

/** Match "Display Name <addr@host>" anywhere in the title.
 *
 *  We deliberately use a SEARCH, not an anchored ^...$ match,
 *  because BenkoBoard / Trello / Outlook all occasionally append
 *  metadata after the closing `>` (truncated subject, date stamp,
 *  trailing whitespace, even a literal `\n`). Operator complaint
 *  2026-05-11 (after qf!2809 shipped): the drawer Email tab still
 *  didn't appear on a freshly-imported Trello card because the
 *  anchored regex from v1 rejected `Rebecca Newman
 *  <rebecca.newman@newmark.com.au>  ` (trailing whitespace +
 *  invisible chars). A search-style regex with a permissive
 *  display-name capture is robust against the long tail of
 *  formatting variants we see on prod. */
const EMAIL_TITLE_RE = /(?<name>[^<>]*?)\s*<(?<addr>[\w.+\-]+@[\w.\-]+\.[A-Za-z]{2,})>/

/** Match the BenkoBoard-style `From <Name> <addr>:` header that
 *  Trello-into-email forwards prepend to every commentCard body.
 *  Real example (see backend `COMMENT_FROM_RE` in
 *  `email_kanban/services/trello_card_email_linker.py`):
 *
 *      From Mark Newman mark.newman@newmark.com.au:
 *
 *      Hi team, can you confirm the variation 14 cost for the
 *      staircase by Friday?
 *
 *  Note the address has NO angle brackets here — it's a bare token
 *  after the display name. This is the exact pattern Trello's
 *  email-into-list integration uses. Keeping the regex character-
 *  for-character compatible with the backend's `COMMENT_FROM_RE`
 *  matters for the linker round-trip: when the backend eventually
 *  parses the same comment and matches it to an Outlook Email row,
 *  the address it pulls must equal the address WE displayed, or the
 *  operator will see a `primary_email` link appear with a different
 *  sender than the comment thread they were just reading.
 *
 *  The `m` flag mirrors the backend's `re.MULTILINE`. */
const COMMENT_FROM_RE =
  /^From\s+(?<name>.+?)\s+(?<addr>[\w.+\-]+@[\w.\-]+\.[A-Za-z]{2,})\s*:\s*\n+/m

/** Strip the BenkoBoard command preamble that's prepended to every
 *  description on imported cards. Format observed on prod:
 *
 *      BenkoBoard Commands:
 *      ----
 *      @reply - Reply to <Name>, <addr>
 *      @forward - Forward to ...
 *      ----
 *
 *  Everything UP TO the second `----` separator is BenkoBoard
 *  control plane, not part of the email body. We keep everything
 *  AFTER the closing separator. When the marker isn't present (e.g.
 *  manually-edited card or non-BenkoBoard import) we return the
 *  input unchanged so we don't silently lose content.
 */
export function stripBenkoCommandPreamble(summary: string | null | undefined): string {
  if (!summary) return ''
  const trimmed = summary.replace(/^\uFEFF/, '').trim()
  // Find a BenkoBoard preamble fenced between two `----` separators.
  // The first `----` typically follows a `BenkoBoard Commands:` line;
  // we require it to land in the first 200 chars so we don't match
  // a separator that lives inside a legitimate quoted reply later in
  // the body.
  const fenceRe = /^([\s\S]{0,200}?)----\r?\n([\s\S]+?)\r?\n----\r?\n?/
  const m = trimmed.match(fenceRe)
  if (m) {
    return trimmed.slice(m[0].length).trimStart()
  }
  return trimmed
}

/** Parse a card title of the form "Display Name <addr@host>" into
 *  the two parts. Returns `null` when the title contains no email
 *  address pattern — caller falls back to "(unknown sender)" rendering.
 *
 *  Permissive: any prefix before `<addr@host>` becomes the display
 *  name; anything after the closing `>` is discarded. Falls back to
 *  using the address local-part as the display name when the title
 *  is JUST an angle-bracketed address (`<rebecca.newman@…>`).
 */
export function parseEmailTitle(
  title: string | null | undefined,
): { name: string; addr: string } | null {
  if (!title) return null
  const m = title.match(EMAIL_TITLE_RE)
  if (!m || !m.groups) return null
  const rawName = (m.groups.name ?? '').trim()
  const addr = m.groups.addr.trim().toLowerCase()
  const name = rawName || addr.split('@')[0]
  return { name, addr }
}

/** Match the webmail "open original" preamble Gmail-/Outlook-into-
 *  Trello forwards prepend to the description. Real example (Newmark
 *  "V gutter detail" card, 2026-06-15):
 *      *View in Gmail
 *      <https://mail.google.com/mail/u/0/#inbox/19e484ac290c9e90>*
 *  Trello wraps the URL in `< >` and the line in markdown `*`. Anchored
 *  at the START so a webmail link buried mid-body (quoted reply) is
 *  never treated as the card's own preamble. Groups: `provider`, `url`. */
const WEBMAIL_PREAMBLE_RE =
  /^\s*\*?\s*View in\s+(?<provider>Gmail|Outlook|Mail|Webmail)\b\s*<?(?<url>https?:\/\/[^\s>*]+)>?\s*\*?\s*\n*/i

/** Map a webmail deep-link host (or the provider word from the
 *  preamble) to a friendly label used on the "Open in …" button. */
function labelForWebmailUrl(url: string, providerWord?: string): string {
  const host = (() => {
    try {
      return new URL(url).host.toLowerCase()
    } catch {
      return ''
    }
  })()
  if (host.includes('mail.google.com') || /gmail/i.test(providerWord ?? '')) return 'Gmail'
  if (host.includes('outlook') || host.includes('live.com') || /outlook/i.test(providerWord ?? ''))
    return 'Outlook'
  return 'webmail'
}

export interface WebmailLink {
  /** Friendly provider label, e.g. "Gmail" / "Outlook" / "webmail". */
  provider: string
  /** The deep link to the original message in the webmail client. */
  url: string
}

/** Extract the "View in <provider> <url>" deep link from a card
 *  description, if present in its leading block. Returns `null` when
 *  the description has no such preamble.
 *
 *  Restricted to the HEAD (the anchored preamble) so a webmail URL
 *  quoted deep inside a reply chain never gets mistaken for the
 *  card's own "open original" affordance. */
export function extractWebmailLink(summary: string | null | undefined): WebmailLink | null {
  if (!summary) return null
  const m = summary.replace(/^\uFEFF/, '').match(WEBMAIL_PREAMBLE_RE)
  if (!m || !m.groups) return null
  const url = m.groups.url.trim()
  return { provider: labelForWebmailUrl(url, m.groups.provider), url }
}

/** Strip the leading "View in <provider> <url>" webmail preamble so
 *  the rendered email body starts at the actual message text. The
 *  deep link itself is surfaced separately (see `extractWebmailLink`)
 *  as a clickable "Open in …" affordance, so dropping it from the
 *  body text is loss-free. No-op when the preamble is absent. */
export function stripWebmailPreamble(summary: string | null | undefined): string {
  if (!summary) return ''
  const trimmed = summary.replace(/^\uFEFF/, '')
  const m = trimmed.match(WEBMAIL_PREAMBLE_RE)
  if (m) return trimmed.slice(m[0].length).trimStart()
  return trimmed.trim()
}

/** True when a card looks like an email even though `primary_email`
 *  is NULL.
 *
 *  Signals (any one triggers true):
 *   - title contains a `<addr@host>` substring (Trello / BenkoBoard
 *     forward shape), OR
 *   - summary begins with `BenkoBoard Commands:` (the canonical
 *     BenkoBoard import preamble), OR
 *   - summary begins with a `View in Gmail/Outlook <url>` webmail
 *     forward preamble (Newmark Gmail forwards — operator complaint
 *     2026-06-15: "where is the email ... I thought we linked emails
 *     from trello"). These carry the whole message in the description
 *     + a deep link to the original.
 *
 *  v2 (qf!2026-05-11) trusts the title/preamble shape even with no
 *  body yet — the EmailFromCard view renders a "body wasn't imported"
 *  empty state, a better signal than hiding the affordance entirely.
 */
export function cardLooksLikeEmail(card: {
  primary_email: string | null
  title: string
  summary: string
}): boolean {
  if (card.primary_email) return false
  if (parseEmailTitle(card.title)) return true
  const head = (card.summary ?? '').trimStart().slice(0, 200)
  if (/BenkoBoard Commands\s*:/i.test(head)) return true
  if (extractWebmailLink(card.summary)) return true
  return false
}

/** True only for Trello-mirrored cards.

    Job-pipeline cards also carry `external_id` (`{boardId}:{jobId}`).
    That key is NOT a Trello card id. The Email tab must not GET
    `/api/trello/cards/<external_id>/` unless `provider === 'trello'`.
 */
export function isTrelloMirrorCard(card: {
  provider?: string | null
  external_id?: string | null
}): boolean {
  return card.provider === 'trello' && Boolean(card.external_id)
}

/** Pure helper: given the raw text of a Trello commentCard, return
 *  `{name, addr, body}` if it carries the BenkoBoard-style email
 *  header, or `null` otherwise.
 *
 *  Mirrors `parse_comment_from_header` in
 *  `email_kanban/services/trello_card_email_linker.py` — same regex,
 *  same lowercasing of `addr` (so the FE-displayed address matches
 *  the BE-stored address used for downstream linking), same split
 *  on header-end to produce the body text.
 *
 *  Returns `null` for empty / undefined / non-matching input so the
 *  caller can fall through to a non-email-shaped comment rendering
 *  (or skip the comment entirely when only email-shaped comments
 *  belong on the Email tab). */
export function parseEmailFromTrelloComment(
  text: string | null | undefined,
): { name: string; addr: string; body: string } | null {
  if (!text) return null
  const m = text.match(COMMENT_FROM_RE)
  if (!m || !m.groups) return null
  const addr = m.groups.addr.trim().toLowerCase()
  const name = (m.groups.name ?? '').trim() || addr.split('@')[0]
  const body = text.slice((m.index ?? 0) + m[0].length).trimEnd()
  return { name, addr, body }
}

/** A normalised email-thread item produced from a single Trello
 *  comment. We separate this from the `TrelloCommentDetail` raw
 *  shape so the renderer doesn't have to know about the comment
 *  header parsing — that's done up front in the pure helper. */
export interface EmailThreadItem {
  /** Stable id from the originating TrelloAction so React keys are
   *  consistent across re-fetches. */
  id: string
  /** Sender display name. Falls back to local-part of `fromAddr`
   *  when the comment header had no name. */
  fromName: string
  /** Lowercased sender address. */
  fromAddr: string
  /** When the comment was posted on Trello (ISO 8601 string). May
   *  be null if Trello didn't return one (very old actions). */
  date: string | null
  /** The plain-text body remaining after the `From <Name> <addr>:`
   *  header is stripped. */
  body: string
}

/** Pure helper: project a Trello comments array into the email
 *  thread items we render on the Email tab. Skips comments that
 *  aren't email-shaped (no `From <Name> <addr>:` header) — those
 *  are operator commentary, not forwarded email content.
 *
 *  Returns oldest-first (chronological reading order — the same
 *  order an inbox thread renders in). Falls back to the array as
 *  given when any `date` is missing (preserve API order). */
export function parseEmailThreadFromComments(
  comments: ReadonlyArray<TrelloCommentDetail> | null | undefined,
): EmailThreadItem[] {
  if (!comments || comments.length === 0) return []
  const items: EmailThreadItem[] = []
  for (const c of comments) {
    const parsed = parseEmailFromTrelloComment(c.body)
    if (!parsed) continue
    items.push({
      id: c.id,
      fromName: parsed.name,
      fromAddr: parsed.addr,
      date: c.date,
      body: parsed.body,
    })
  }
  if (items.every((i) => i.date)) {
    items.sort((a, b) => (a.date! < b.date! ? -1 : a.date! > b.date! ? 1 : 0))
  }
  return items
}
